import { Button, Flex, Icon, SimpleGrid, useToast } from '@chakra-ui/react';
import { MdRefresh } from 'react-icons/md';
import { MdTrendingDown } from 'react-icons/md';
import { ChartIllustration } from '../../components/illustrations';
import PageLayout from '../../components/page-layout';
import PageCard from '../../components/page-card';
import EmptyState from '../../components/states/EmptyState';
import SkeletonCardGrid from '../../components/states/SkeletonCardGrid';
import { translate } from '../../i18n';
import { useSelectedOrganization } from '../organization/hooks/useSelectedOrganization';
import ScopePrompt from '../_shared/ScopePrompt';
import { classifyRead } from '../_shared/readState';
import { useGetChurnDistribution } from './hooks/useGetChurnDistribution';
import { useGetSmartCohorts } from './hooks/useGetSmartCohorts';
import { useRecomputeChurnScores } from './hooks/useRecomputeChurnScores';
import ChurnTierDistributionCards from './components/ChurnTierDistributionCards';
import ChurnDistributionChart from './components/ChurnDistributionChart';
import SmartCohortCards from './components/SmartCohortCards';
import ChurnUsersTable from './components/ChurnUsersTable';

export default function ChurnPredictionPage() {
  const { selectedOrganization } = useSelectedOrganization();
  const orgId = selectedOrganization?.id;
  const toast = useToast();

  // Both reads are `enabled: Boolean(organizationId)` and this page carried NO
  // scope guard, so with no organization selected React Query v4 held
  // `isLoading: true` / `fetchStatus: 'idle'` and the four-card skeleton below
  // was permanent — the whole page was a loading affordance for a read nobody
  // sent. `isInitialLoading` (`isLoading && isFetching`) is false for a
  // disabled query, which is the flag the raw one should always have been.
  const { data: distribution, isInitialLoading: isDistributionLoading, isError: isDistributionError } = useGetChurnDistribution(orgId);
  const { data: cohorts, isInitialLoading: isCohortsLoading, isError: isCohortsError } = useGetSmartCohorts(orgId);
  const recompute = useRecomputeChurnScores(orgId || '');

  const readState = classifyRead(isDistributionError, isDistributionLoading, distribution);

  const handleRecompute = async () => {
    try {
      await recompute.mutateAsync();
      toast({
        title: translate('churn_prediction.recompute_success'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: translate('churn_prediction.recompute_error'),
        description: error instanceof Error ? error.message : undefined,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const isEmpty = distribution && distribution.total === 0;

  return (
    <PageLayout>
      <PageCard
        title={translate('churn_prediction.title')}
        rightHeader={
          <Button
            leftIcon={<Icon as={MdRefresh} />}
            variant="brand"
            size="sm"
            onClick={handleRecompute}
            isLoading={recompute.isLoading}
          >
            {translate('churn_prediction.recompute')}
          </Button>
        }
      >
        {readState === 'never-issued' ? (
          <ScopePrompt gate='organization' />
        ) : readState === 'loading' ? (
          <SkeletonCardGrid cards={4} columns={{ base: 1, md: 2, lg: 4 }} />
        ) : readState === 'unreadable' ? (
          // `unreadable`, not the raw `isDistributionError`: a background
          // refetch that fails over a distribution already on screen keeps the
          // cards and the table below rather than replacing them with an error
          // panel (R96-3).
          <EmptyState
            title={translate('churn_prediction.error_loading')}
            illustration={<ChartIllustration />}
          />
        ) : isEmpty ? (
          <EmptyState
            variant="first-use"
            illustration={<ChartIllustration />}
            title={translate('churn_prediction.empty_state')}
            actionLabel={translate('churn_prediction.recompute')}
            onAction={handleRecompute}
          />
        ) : null}
      </PageCard>

      {distribution && !isEmpty && (
        <>
          <ChurnTierDistributionCards distribution={distribution} />

          <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
            <ChurnDistributionChart distribution={distribution} />
            <SmartCohortCards cohorts={cohorts || []} isLoading={isCohortsLoading} isError={isCohortsError} />
          </SimpleGrid>

          <ChurnUsersTable />
        </>
      )}
    </PageLayout>
  );
}
